import React, { useMemo, useState, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import { Grid, Typography, IconButton, Menu, MenuItem, Box } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList
} from "recharts";

import SkeletonTotalGrowthBarChart from "./ui-component/cards/Skeleton/TotalGrowthBarChart";
import MainCard from "./ui-component/cards/MainCard";

interface TotalGrowthBarChartProps {
  isLoading: boolean;
  dataChartCustomersList: number[];
  dataCharDriverList: number[];
  dataChartVehiclesList: number[];
}

const months = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const typographyPrometo = { fontFamily: "Prometo, sans-serif" };

const CustomTooltip = ({ active, payload, label, theme }: any) => {
  if (active && payload && payload.length) {
    return (
      <div 
        style={{ 
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
          padding: '12px', 
          borderRadius: '8px', 
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.palette.divider}`,
          ...typographyPrometo
        }}
      >
        <Typography variant="subtitle2" style={{ ...typographyPrometo, fontWeight: 'bold', color: theme.palette.text.primary, marginBottom: 8 }}>
          {label}
        </Typography>
        {payload.map((entry: any, index: number) => (
          <Typography key={index} variant="body2" style={{ ...typographyPrometo, color: entry.fill, fontWeight: 500 }}>
            {entry.name}: {entry.value}
          </Typography>
        ))}
      </div>
    );
  }
  return null;
};

const TotalGrowthBarChart = ({
  isLoading,
  dataChartCustomersList,
  dataCharDriverList,
  dataChartVehiclesList
}: TotalGrowthBarChartProps) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Aesthetic System Theme (Imagem Referência: Roxo, Ciano, Prata)
  const colorClientes = "#b0bec5"; // Prata/Cinza
  const colorVeiculos = "#00bcd4"; // Ciano
  const colorMotoristas = "#5e35b1"; // Roxo

  const formattedData = useMemo(() => {
    return months.map((month, index) => ({
      name: month,
      "Clientes": dataChartCustomersList?.[index] || 0,
      "Veículos": dataChartVehiclesList?.[index] || 0,
      "Motoristas": dataCharDriverList?.[index] || 0,
    }));
  }, [dataChartCustomersList, dataChartVehiclesList, dataCharDriverList]);

  const isEmpty = useMemo(() => {
    return !formattedData.some((d) => d["Clientes"] > 0 || d["Veículos"] > 0 || d["Motoristas"] > 0);
  }, [formattedData]);

  const handleLegendClick = (name: string) => {
    if (hiddenSeries.includes(name)) {
      setHiddenSeries(hiddenSeries.filter((k) => k !== name));
    } else {
      setHiddenSeries([...hiddenSeries, name]);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const exportCSV = () => {
    const csvContent = [
      "Mês,Clientes,Veículos,Motoristas",
      ...formattedData.map(d => `${d.name},${d["Clientes"]},${d["Veículos"]},${d["Motoristas"]}`)
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "chart_manager_export.csv";
    link.click();
    handleMenuClose();
  };

  if (isLoading) {
    return <SkeletonTotalGrowthBarChart />;
  }

  if (isEmpty) {
    return (
      <Grid className="dashboard-chart-surface tw-rounded-xl tw-bg-slate-50 dark:tw-bg-slate-900" container justifyContent="center" alignItems="center" style={{ minHeight: 350, background: "#f9fafb", borderRadius: 12, boxShadow: "none" }}>
        <Typography variant="h5" color="textSecondary" style={typographyPrometo}>
          Nenhum dado disponível para visualização.
        </Typography>
      </Grid>
    );
  }

  const renderInteractiveLegend = () => {
    const categories = [
      { name: "Clientes", color: colorClientes },
      { name: "Veículos", color: colorVeiculos },
      { name: "Motoristas", color: colorMotoristas }
    ];
    return (
      <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} pt={2}>
        {categories.map((cat, index) => {
          const isHidden = hiddenSeries.includes(cat.name);
          return (
            <Box 
              key={index} 
              onClick={() => handleLegendClick(cat.name)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', opacity: isHidden ? 0.4 : 1, transition: '0.2s' }}
            >
              <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: isHidden ? '#ccc' : cat.color, mr: 1 }} />
              <Typography variant="body2" sx={{ ...typographyPrometo, color: theme.palette.text.secondary, textDecoration: isHidden ? 'line-through' : 'none' }}>
                {cat.name}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    if (value <= 0) return null;
    return (
      <text x={x + width / 2} y={y + height / 2} fill="#fff" dominantBaseline="middle" textAnchor="middle" style={{ ...typographyPrometo, fontSize: 11, fontWeight: 500 }}>
        {value}
      </text>
    );
  };

  return (
    <div className="dashboard-chart-surface tw-relative tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-slate-50 dark:tw-bg-slate-900" style={{ position: "relative", height: "clamp(350px, 40vw, 440px)", width: "100%", background: "#f9fafb", borderRadius: 12, outline: "none" }} ref={chartRef}>
      <style>{`.dashboard-chart-surface .recharts-wrapper, .dashboard-chart-surface .recharts-surface { outline: none !important; }`}</style>
      
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pt={2}>
        <Typography variant="h6" style={{ ...typographyPrometo, fontWeight: 600, color: theme.palette.success.main || "#00b700" }}>
          Barras Combinadas Totais
        </Typography>
        <Box>
          <IconButton className="!tw-bg-white/80 !tw-text-gtr-teal tw-shadow-sm hover:!tw-bg-white dark:!tw-bg-slate-800 dark:!tw-text-slate-100 dark:hover:!tw-bg-slate-700" onClick={handleMenuClick} size="small">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
            </svg>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={exportCSV} style={typographyPrometo}>Download CSV</MenuItem>
          </Menu>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={formattedData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
          <XAxis dataKey="name" interval="preserveStartEnd" minTickGap={12} tick={{ fill: theme.palette.text.secondary, fontSize: 11, ...typographyPrometo }} axisLine={{ stroke: theme.palette.divider }} tickLine={false} />
          <YAxis tick={{ fill: theme.palette.text.secondary, fontSize: 12, ...typographyPrometo }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip theme={theme} />} cursor={{ fill: theme.palette.action.hover, opacity: 0.15 }} />
          
          <Bar hide={hiddenSeries.includes("Clientes")} dataKey="Clientes" stackId="a" fill={colorClientes} animationDuration={600} maxBarSize={45}>
            <LabelList dataKey="Clientes" content={renderLabel} />
          </Bar>
          <Bar hide={hiddenSeries.includes("Veículos")} dataKey="Veículos" stackId="a" fill={colorVeiculos} animationDuration={600} maxBarSize={45}>
            <LabelList dataKey="Veículos" content={renderLabel} />
          </Bar>
          <Bar hide={hiddenSeries.includes("Motoristas")} dataKey="Motoristas" stackId="a" fill={colorMotoristas} animationDuration={600} maxBarSize={45} radius={[4, 4, 0, 0]}>
            <LabelList dataKey="Motoristas" content={renderLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      </Box>
      <Box sx={{ width: "100%", maxHeight: 76, overflowY: 'auto', pb: 2 }}>
         {renderInteractiveLegend()}
      </Box>
    </div>
  );
};

export default TotalGrowthBarChart;
